/**
 * AdminLayout Component
 *
 * Admin panel layout with collapsible sidebar matching the main app Layout.
 * Distinct visual styling (red/amber accent) to clearly distinguish from
 * the main SpeyBooks application.
 *
 * Collapse behaviour:
 *   - Persisted to localStorage (separate key from main app)
 *   - Cmd/Ctrl+B keyboard shortcut
 *   - Chevron toggle button on sidebar edge
 *   - Favicon shown when collapsed, full logo when expanded
 *
 * v4.7.5 — collapsible sidebar
 */

import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, ThemeIcons } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import OmniSearch from './OmniSearch';

// ============================================================================
// Constants
// ============================================================================

const SIDEBAR_COLLAPSED_KEY = 'speybooks_admin_sidebar_collapsed';

// ============================================================================
// Admin Icons
// ============================================================================

const AdminIcons = {
  Dashboard: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Users: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Organisations: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  Settings: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  AuditLog: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  Health: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  BugReports: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  BackToApp: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
    </svg>
  ),
  Menu: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  Close: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
};

// ============================================================================
// Navigation
// ============================================================================

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: AdminIcons.Dashboard },
  { name: 'Organisations', href: '/admin/orgs', icon: AdminIcons.Organisations },
  { name: 'Users', href: '/admin/users', icon: AdminIcons.Users },
  { name: 'Settings', href: '/admin/settings', icon: AdminIcons.Settings },
  { name: 'Audit Log', href: '/admin/audit-log', icon: AdminIcons.AuditLog },
  { name: 'System Health', href: '/admin/health', icon: AdminIcons.Health },
  { name: 'Bug Reports', href: '/admin/bug-reports', icon: AdminIcons.BugReports },
];

// ============================================================================
// Sidebar Nav Item
// ============================================================================

function SidebarNavItem({
  item,
  collapsed,
  onClick,
}: {
  item: (typeof navigation)[number];
  collapsed: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={item.href}
      end={item.href === '/admin'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg text-sm font-medium mb-1 transition-colors',
          collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5',
          isActive
            ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
            : 'text-content-secondary hover:bg-surface-hover'
        )
      }
      title={collapsed ? item.name : undefined}
    >
      <item.icon />
      {!collapsed && <span>{item.name}</span>}
    </NavLink>
  );
}

// ============================================================================
// Theme Toggle
// ============================================================================

function ThemeToggle({ collapsed = false }: { collapsed?: boolean }) {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    { value: 'light' as const, label: 'Light', icon: ThemeIcons.Sun },
    { value: 'dark' as const, label: 'Dark', icon: ThemeIcons.Moon },
    { value: 'system' as const, label: 'System', icon: ThemeIcons.System },
  ];

  const currentOption = options.find(o => o.value === theme) || options[2];

  if (collapsed) {
    return (
      <button
        onClick={() => {
          const idx = options.findIndex(o => o.value === theme);
          setTheme(options[(idx + 1) % options.length].value);
        }}
        className="flex items-center justify-center w-full px-2 py-2 text-content-secondary hover:bg-surface-hover rounded-lg transition-colors"
        title={`Theme: ${currentOption.label}`}
      >
        <currentOption.icon />
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-content-secondary hover:bg-surface-hover rounded-lg transition-colors"
        title="Change theme"
      >
        <currentOption.icon />
        <span>{currentOption.label}</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 bottom-full mb-1 w-36 bg-surface rounded-lg shadow-lg border border-edge py-1 z-20">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  setTheme(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors',
                  theme === option.value
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    : 'text-content-secondary hover:bg-surface-hover'
                )}
              >
                <option.icon />
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Layout
// ============================================================================

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; }
    catch { return false; }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Persist collapse state
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed)); }
    catch { /* storage full */ }
  }, [sidebarCollapsed]);

  // Cmd/Ctrl+B to toggle sidebar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Cmd/Ctrl+K for search
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setSearchOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleBackToApp = () => {
    navigate('/');
  };

  return (
    <div className="h-screen flex flex-col bg-surface-secondary overflow-hidden">
      {/* Admin accent bar */}
      <div className="shrink-0 h-1 bg-gradient-to-r from-red-500 via-amber-500 to-red-500 z-50" />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar — fixed overlay */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-surface transform transition-transform duration-300 ease-in-out lg:hidden pt-1',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-edge">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">SB</span>
            </div>
            <span className="font-semibold text-content">Admin</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="text-content-tertiary">
            <AdminIcons.Close />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <SidebarNavItem key={item.name} item={item} collapsed={false} onClick={() => setSidebarOpen(false)} />
          ))}
        </nav>
      </div>

      {/* Main row */}
      <div className="flex flex-1 min-h-0">

        {/* Desktop sidebar */}
        <aside
          className={cn(
            'relative hidden lg:flex lg:flex-col shrink-0 bg-surface border-r border-edge transition-all duration-300 ease-in-out',
            sidebarCollapsed ? 'w-16' : 'w-64'
          )}
        >
          {/* Collapse toggle chevron */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-[72px] z-10 w-6 h-6 rounded-full bg-surface border border-edge shadow-sm flex items-center justify-center text-content-tertiary hover:text-content-secondary hover:bg-surface-hover transition-colors"
            title={sidebarCollapsed ? 'Expand sidebar (\u2318B)' : 'Collapse sidebar (\u2318B)'}
          >
            <svg
              className={cn('w-3 h-3 transition-transform duration-300', sidebarCollapsed && 'rotate-180')}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Logo / Favicon */}
          <div className={cn(
            'flex items-center h-16 border-b border-edge overflow-hidden transition-all duration-300',
            sidebarCollapsed ? 'px-3 justify-center' : 'px-6'
          )}>
            {sidebarCollapsed ? (
              <div className="w-7 h-7 shrink-0">
                <img src="/favicon.svg" alt="SpeyBooks" className="w-9 h-9" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-amber-500 flex items-center justify-center shadow-lg shrink-0">
                  <span className="text-white font-bold">SB</span>
                </div>
                <div>
                  <span className="font-semibold text-content">SpeyBooks</span>
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                    Admin
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Search button */}
          {!sidebarCollapsed && (
            <div className="px-3 py-3 border-b border-edge">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-3 w-full px-3 py-2 text-sm text-content-tertiary bg-surface-tertiary rounded-lg hover:bg-surface-active transition-colors"
              >
                <AdminIcons.Search />
                <span>Search...</span>
                <kbd className="ml-auto px-2 py-0.5 text-xs bg-surface rounded border border-edge-strong">
                  ⌘K
                </kbd>
              </button>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="px-2 py-3 border-b border-edge">
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center justify-center w-full px-2 py-2 text-content-tertiary hover:bg-surface-hover rounded-lg transition-colors"
                title="Search (⌘K)"
              >
                <AdminIcons.Search />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className={cn('flex-1 mt-4', sidebarCollapsed ? 'px-2' : 'px-3')}>
            {navigation.map((item) => (
              <SidebarNavItem key={item.name} item={item} collapsed={sidebarCollapsed} />
            ))}
          </nav>

          {/* Footer */}
          <div className={cn('border-t border-edge', sidebarCollapsed ? 'p-2' : 'p-4')}>
            {/* Back to app */}
            <button
              onClick={handleBackToApp}
              className={cn(
                'flex items-center rounded-lg text-sm text-content-secondary hover:bg-surface-hover transition-colors mb-2',
                sidebarCollapsed ? 'justify-center w-full px-2 py-2' : 'gap-2 w-full px-3 py-2'
              )}
              title={sidebarCollapsed ? 'Back to App' : undefined}
            >
              <AdminIcons.BackToApp />
              {!sidebarCollapsed && <span>Back to App</span>}
            </button>

            {/* User info */}
            {sidebarCollapsed ? (
              <div className="flex justify-center mb-2">
                <div
                  className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center"
                  title={user?.fullName || 'Admin'}
                >
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {user?.fullName?.charAt(0) || 'A'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0">
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {user?.fullName?.charAt(0) || 'A'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-content truncate">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Platform Admin</p>
                </div>
              </div>
            )}

            <ThemeToggle collapsed={sidebarCollapsed} />
          </div>
        </aside>

        {/* Content column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header */}
          <div className="flex items-center h-16 px-4 bg-surface border-b border-edge lg:hidden shrink-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-content-tertiary hover:text-content-secondary"
            >
              <AdminIcons.Menu />
            </button>
            <span className="ml-4 text-lg font-semibold text-content">
              {navigation.find((item) => {
                if (item.href === '/admin') return location.pathname === '/admin';
                return location.pathname.startsWith(item.href);
              })?.name || 'Admin'}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-content-tertiary hover:text-content-secondary"
              >
                <AdminIcons.Search />
              </button>
              <ThemeToggle />
            </div>
          </div>

          {/* Scrollable content */}
          <main className="flex-1 overflow-auto min-h-0 p-4 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Omni-search modal */}
      <OmniSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
